import { memo, useEffect, useRef } from "react";
import { ColumnCellProps, TableHeaderProps } from "../../../../types";
import { getWidth } from "../../utils";
import DecrementIcon from "../../utils/DecrementIcon";
import DefaultIcon from "../../utils/DefaultIcon";
import IncrementIcon from "../../utils/IncrementIcon";
import { alignItemPositions } from "../TableCell";

function ColumnCell({
  index,
  col,
  enableColumnDrag,
  onColumnDragStart,
  onColumnDragOver,
  onColumnDrop,
  handleMouseDown,
  stickyClass,
  columnType,
  selectAll,
  deselectAll,
  isAllRowsSelected = false,
  showIntermediateStateOnCheckbox,
  setSortingColumns,
  sortingColumns,
}: ColumnCellProps) {
  const checkboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = showIntermediateStateOnCheckbox;
    }
  }, [showIntermediateStateOnCheckbox]);

  const handleCheckboxChange = () => {
    if (checkboxRef.current?.checked || showIntermediateStateOnCheckbox) {
      selectAll();
    } else if (!checkboxRef.current?.checked) {
      deselectAll();
    }
  };

  const handleClick = () => {
    if (col.sorting) {
      setSortingColumns(
        sortingColumns.map((item) => {
          if (item.field === col.field) {
            let newSortType;
            switch (item.sortType) {
              case "asc":
                newSortType = "desc";
                break;
              case "desc":
                newSortType = "default";
                break;
              default:
                newSortType = "asc";
                break;
            }
            return { ...item, sortType: newSortType };
          }
          return { ...item, sortType: "default" };
        })
      );
    }
  };

  return (
    <div
      key={col.field}
      className={`div-grid-cell header-cell ${
        col.field === "selection" && "div-grid-cell-checkbox"
      } ${stickyClass}`}
      style={
        col.field === "selection"
          ? { minWidth: col.width, width: col.width }
          : {
              ...getWidth(col.width),
              justifyContent: col.alignItem
                ? alignItemPositions[col.alignItem]
                : alignItemPositions.left,
            }
      }
      draggable={enableColumnDrag}
      onDragStart={() => {
        onColumnDragStart(index);
      }}
      onDragOver={(e) => {
        onColumnDragOver(index, e);
      }}
      onDrop={() => {
        onColumnDrop(index);
      }}
      onClick={handleClick}
    >
      {col.field === "selection" && (
        <div>
          <input
            type="checkbox"
            ref={checkboxRef}
            checked={isAllRowsSelected}
            onChange={handleCheckboxChange}
            aria-label="Select All Top-Level Rows"
          />
        </div>
      )}
      {col.field !== "selection" && (
        <div style={{ display: "flex", gap: "4px" }}>
          <span>{col.headerName}</span>
          {sortingColumns.some((e) => e.field === col.field) && (
            <>
              {sortingColumns.map((e) => {
                if (e.field === col.field) {
                  switch (e.sortType) {
                    case "asc":
                      return <IncrementIcon />;
                    case "desc":
                      return <DecrementIcon />;

                    default:
                      return <DefaultIcon />;
                  }
                }
                return null;
              })}
            </>
          )}
        </div>
      )}
      {col.field !== "selection" && (
        <div
          className="resize-handle"
          onMouseDown={(e) => {
            if (col.field !== "selection") {
              handleMouseDown?.(index, e, columnType);
            }
          }}
        />
      )}
    </div>
  );
}

const TableHeader = (props: TableHeaderProps) => {
  const {
    leftColumns,
    mainColumns,
    rightColumns,
    mainContentRef,
    handleMouseDown,
    enableColumnDrag,
    onColumnDragStart,
    onColumnDragOver,
    onColumnDrop,
    selectAll,
    deselectAll,
    isAllRowsSelected = false,
    showIntermediateStateOnCheckbox,
    sortingColumns,
    setSortingColumns,
    leftMargin,
    rightMargin,
  } = props;

  return (
    <div className="div-grid-header">
      {!!leftColumns.length && (
        <div className="div-grid-row sticky-column-container sticky-column-header left-sticky-columns">
          {leftColumns.map((col, index) => (
            <ColumnCell
              col={col}
              handleMouseDown={handleMouseDown}
              enableColumnDrag={enableColumnDrag}
              onColumnDragStart={onColumnDragStart}
              onColumnDragOver={onColumnDragOver}
              onColumnDrop={onColumnDrop}
              index={index}
              stickyClass="sticky-left"
              columnType="left"
              selectAll={selectAll}
              deselectAll={deselectAll}
              isAllRowsSelected={isAllRowsSelected}
              showIntermediateStateOnCheckbox={showIntermediateStateOnCheckbox}
              sortingColumns={sortingColumns}
              setSortingColumns={setSortingColumns}
              key={col.field}
            />
          ))}
        </div>
      )}

      <div
        className="div-grid-row main-content-header hide-scrollbar"
        ref={mainContentRef}
        style={{
          overflow: "scroll",
          marginLeft: leftMargin,
          marginRight: rightMargin,
        }}
      >
        {mainColumns.map((col, index) => (
          <ColumnCell
            col={col}
            handleMouseDown={handleMouseDown}
            enableColumnDrag={enableColumnDrag}
            onColumnDragStart={onColumnDragStart}
            onColumnDragOver={onColumnDragOver}
            onColumnDrop={onColumnDrop}
            index={index}
            columnType="main"
            selectAll={selectAll}
            deselectAll={deselectAll}
            isAllRowsSelected={isAllRowsSelected}
            showIntermediateStateOnCheckbox={showIntermediateStateOnCheckbox}
            sortingColumns={sortingColumns}
            setSortingColumns={setSortingColumns}
            key={col.field}
          />
        ))}
      </div>

      {!!rightColumns.length && (
        <div className="div-grid-row sticky-column-container sticky-column-header right-sticky-columns">
          {rightColumns.map((col, index) => (
            <ColumnCell
              col={col}
              handleMouseDown={handleMouseDown}
              enableColumnDrag={enableColumnDrag}
              onColumnDragStart={onColumnDragStart}
              onColumnDragOver={onColumnDragOver}
              onColumnDrop={onColumnDrop}
              index={index}
              columnType="right"
              stickyClass="sticky-right"
              selectAll={selectAll}
              deselectAll={deselectAll}
              isAllRowsSelected={isAllRowsSelected}
              showIntermediateStateOnCheckbox={showIntermediateStateOnCheckbox}
              sortingColumns={sortingColumns}
              setSortingColumns={setSortingColumns}
              key={col.field}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(TableHeader);
